import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Autocomplete,
  GoogleMap,
  Marker,
  useLoadScript,
} from '@react-google-maps/api';

import { ReactComponent as LocationIcon } from '../../../../../../assets/img/location.svg';
import PointIcon from '../../../../../../assets/img/point-orange.png';
import FloatingButton from '../../../../../../components/FloatingButton';
import Loader from '../../../../../../components/LoaderSplash/Loader.component';
import { NumberFormatCustom } from '../../../../../../components/NumberFormartCustom/NumberFormatCustom.component';
import {
  DEFAULT_CENTER,
  ZOOM_DEFAULT,
} from '../../../../../../constants/maps.constant';
import { useSinisterInspectionsContext } from '../../../../../../context/SinisterInspections/SinisterInspections.provider';
import { useAuthDataContext } from '../../../../../../context/auth/auth.provider';
import { dataLayerPush } from '../../../../../../helpers/dataLayerPush';
import useForm from '../../../../../../helpers/useForm';
import SelectPlaceValidations from './Step3.validations';

const libraries: (
  | 'places'
  | 'drawing'
  | 'geometry'
  | 'localContext'
  | 'visualization'
)[] = ['places'];

const Step3 = () => {
  const {
    step,
    goBack,
    nextStep,
    updateSinisterInspectionBody,
    sinister_inspection_body,
  } = useSinisterInspectionsContext();

  const { user } = useAuthDataContext();
  const [origin, setOrigin] = useState<any>(
    sinister_inspection_body.pickup_location?.latitude &&
      sinister_inspection_body.pickup_location?.longitude && {
        lat: sinister_inspection_body.pickup_location.latitude,
        lng: sinister_inspection_body.pickup_location.longitude,
      }
  );
  const [, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [optional, setOptional] = useState(
    sinister_inspection_body?.optional || false
  );

  const [defaultValues, setDefaults] = useState({
    street: sinister_inspection_body?.pickup_location?.street || '',
    street_number:
      sinister_inspection_body?.pickup_location?.street_number || '',
    locality: sinister_inspection_body?.pickup_location?.locality || '',
    province: sinister_inspection_body?.pickup_location?.province || '',
  });

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // ,
    libraries,
    language: 'es',
    region: 'ar',
  });
  const originRef = useRef<Autocomplete>(null);
  const originInputRef = useRef<HTMLInputElement>(null);

  const Geocoder = window?.google?.maps
    ? new window.google.maps.Geocoder()
    : undefined;

  const getGeocoderResult = useCallback(
    (location: google.maps.LatLngLiteral | google.maps.LatLng) => {
      setLoading(true);
      Geocoder?.geocode({ location }, (results: any, status: any) => {
        setLoading(false);
        if (status === window.google.maps.GeocoderStatus.OK) {
          if (results.length) {
            const pickup_location = getBodyAddress(results[0]);
            if (originInputRef.current) {
              originInputRef.current.value = pickup_location.description;
            }
            updateSinisterInspectionBody({
              pickup_location,
            });
            setOrigin(location);
          } else {
            window.alert('No results found');
          }
        }
      });
    },
    [Geocoder, updateSinisterInspectionBody]
  );

  const initializeOrigin = useCallback(
    (position: any) => {
      const location: google.maps.LatLngLiteral = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };

      if (!origin) {
        getGeocoderResult(location);
      }
    },
    [getGeocoderResult, origin]
  );

  useEffect(() => {
    if (Geocoder && !origin) {
      try {
        setInitialized((ini) => {
          if (!ini) {
            navigator.geolocation.getCurrentPosition(
              initializeOrigin,
              () => {}
            );
          }

          return true;
        });
      } catch (e) {}
    }
  }, [Geocoder, origin, initializeOrigin]);

  useEffect(() => {
    if (sinister_inspection_body.pickup_location) {
      setOrigin({
        lat: sinister_inspection_body.pickup_location.latitude,
        lng: sinister_inspection_body.pickup_location.longitude,
      });
    }
  }, [sinister_inspection_body.pickup_location]);

  const getBodyAddress = (place: any) => {
    const latitude = place?.geometry?.location.lat();
    const longitude = place?.geometry?.location.lng();
    const description = place.formatted_address;
    const street =
      place.address_components?.find(
        (component: any) =>
          !!component.types.find((type: string) => type === 'route')
      )?.long_name || '';
    const street_number =
      place.address_components?.find(
        (component: any) =>
          !!component.types.find((type: string) => type === 'street_number')
      )?.long_name || '';
    const country = place.address_components?.find(
      (component: any) =>
        !!component.types.find((type: string) => type === 'country')
    )?.long_name;
    const province = place.address_components?.find(
      (component: any) =>
        !!component.types.find(
          (type: string) => type === 'administrative_area_level_1'
        )
    )?.long_name;
    const locality =
      place.address_components?.find(
        (component: any) =>
          !!component.types.find((type: string) => type === 'locality')
      )?.long_name ||
      place.address_components?.find(
        (component: any) =>
          !!component.types.find(
            (type: string) => type === 'sublocality_level_1'
          )
      )?.long_name ||
      '';

    setDefaults({
      street,
      street_number,
      locality,
      province,
    });
    return {
      latitude,
      longitude,
      description,
      street,
      street_number,
      country,
      province,
      locality,
    };
  };

  const onClickMap = useCallback(
    (e: google.maps.MouseEvent) => {
      setLoading(true);
      getGeocoderResult(e.latLng);
    },
    [getGeocoderResult]
  );

  const onOriginAutocompleteChange = () => {
    if (originRef !== null) {
      // console.log(originRef.current);
      const place = originRef?.current?.state?.autocomplete?.getPlace();
      const lat = place?.geometry?.location.lat();
      const lng = place?.geometry?.location.lng();
      updateSinisterInspectionBody({
        pickup_location: place && lat && lng && getBodyAddress(place),
      });
      setOrigin(place && lat && lng && { lat, lng });
    }
  };

  const callNextStep = useCallback(
    (values: any) => {
      if (values.street && values.street_number && values.locality) {
        updateSinisterInspectionBody({
          optional: optional,
          pickup_location: {
            ...sinister_inspection_body.pickup_location,
            ...values,
          },
        });
        dataLayerPush('siniestro_ubicación_continuar', user?.id);
        nextStep();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      nextStep,
      sinister_inspection_body.pickup_location,
      updateSinisterInspectionBody,
    ]
  );

  const { values, errors, handleChange, handleSubmit } = useForm(
    callNextStep,
    SelectPlaceValidations,
    defaultValues
  );

  //console.log(sinister_inspection_body, "step3");

  return (
    <Grid container style={{ height: '100%', width: '100%' }}>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Box mr={4}>
          <Box my={4} ml={8}>
            <Typography variant="h3">
              Lugar a realizarse la inspección
            </Typography>
          </Box>
          <Box my={4} ml={8}>
            <Link onClick={() => setOptional(!optional)} underline="hover">
              <Typography variant="caption" color="secondary">
                La dirección no está dentro de las opciones
              </Typography>
            </Link>
            <Box></Box>
          </Box>
          <Box my={4} ml={8}>
            {isLoaded && !optional ? (
              <Box>
                <Autocomplete
                  ref={originRef}
                  onPlaceChanged={onOriginAutocompleteChange}
                  options={{
                    types: ['address'],
                    componentRestrictions: { country: 'ar' },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2">Dirección</Typography>
                    <Box mb="32px">
                      <TextField
                        inputRef={originInputRef}
                        size="small"
                        error={!origin}
                        helperText={
                          !origin
                            ? 'Debes ingresar tu ubicación'
                            : 'Podés seleccionar la ubicación en el mapa'
                        }
                        name="origin"
                        id="origin"
                        placeholder="Tu ubicación"
                        variant="outlined"
                        defaultValue={
                          sinister_inspection_body?.pickup_location
                            ?.description || ''
                        }
                        inputProps={{
                          style: { paddingRight: '30px' },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <LocationIcon />
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Autocomplete>
              </Box>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2">Provincia*</Typography>
                  <Box>
                    <TextField
                      value={values.province}
                      onChange={handleChange}
                      helperText={errors.province || ''}
                      size="small"
                      error={!!errors.province}
                      name="province"
                      fullWidth={true}
                      id="origin"
                      placeholder="Provincia"
                      variant="outlined"
                      inputProps={{
                        style: { paddingRight: '30px' },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <LocationIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Localidad*</Typography>
                  <Box>
                    <TextField
                      value={values.locality}
                      onChange={handleChange}
                      helperText={errors.locality || ''}
                      size="small"
                      error={!!errors.locality}
                      name="locality"
                      fullWidth={true}
                      id="origin"
                      placeholder="Localidad"
                      variant="outlined"
                      inputProps={{
                        style: { paddingRight: '30px' },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <LocationIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2">Calle*</Typography>
                  <Box>
                    <TextField
                      value={values.street}
                      onChange={handleChange}
                      helperText={errors.street || ''}
                      size="small"
                      error={!!errors.street}
                      name="street"
                      fullWidth={true}
                      id="origin"
                      placeholder="Calle*"
                      variant="outlined"
                      inputProps={{
                        style: { paddingRight: '30px' },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <LocationIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2">Número de calle*</Typography>
                  <Box>
                    <TextField
                      InputProps={{
                        inputComponent: NumberFormatCustom as any,
                        endAdornment: (
                          <InputAdornment position="end">
                            <LocationIcon />
                          </InputAdornment>
                        ),
                      }}
                      value={values.street_number}
                      onChange={handleChange}
                      helperText={errors.street_number || ''}
                      size="small"
                      error={!!errors.street_number}
                      name="street_number"
                      fullWidth={true}
                      id="origin"
                      placeholder="Número de calle*"
                      variant="outlined"
                      inputProps={{
                        style: { paddingRight: '30px' },
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
          <Box ml={8}>
            <FloatingButton
              onClick={handleSubmit}
              disabled={
                !optional
                  ? !origin || loading || !!Object.keys(errors).length
                  : !!Object.keys(errors).length ||
                    !values.street ||
                    !values.street_number ||
                    !values.locality ||
                    !values.province
              }
            >
              Continuar
            </FloatingButton>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6} md={8} lg={9}>
        <Box position="relative" width="100%" height="100%">
          <Box display={!isLoaded || loading ? 'block' : 'none'}>
            <Loader action={'Cargando mapa'}></Loader>
          </Box>
          {isLoaded && !optional ? (
            <GoogleMap
              id="assitance-map"
              mapContainerStyle={{
                minHeight: '450px',
                height: '100%',
                width: '100%',
              }}
              center={origin ? origin : DEFAULT_CENTER}
              zoom={ZOOM_DEFAULT}
              onClick={onClickMap}
            >
              {origin ? (
                <Marker
                  icon={PointIcon}
                  position={origin}
                  label={'Aquí'}
                  title={'Aquí'}
                />
              ) : null}
            </GoogleMap>
          ) : null}
        </Box>
      </Grid>
    </Grid>
  );
};

export default Step3;
